import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useBlogComments } from '../../hooks/useBlogComments'
import { useAuth } from '../../context/AuthContext'

const DRAFT_COMMENT_KEY = 'ai4solar_draft_comment'

function CommentAvatar({ name }) {
  const initials = name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)

  return (
    <div className="comment-avatar">
      {initials}
    </div>
  )
}

function Comment({ comment, onLike, hasLiked, likeCount, onDelete, canDelete }) {
  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  return (
    <motion.div
      className="comment"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <CommentAvatar name={comment.author} />
      <div className="comment-body">
        <div className="comment-header">
          <span className="comment-author">{comment.author}</span>
          <span className="comment-date">{formatDate(comment.date)}</span>
        </div>
        <p className="comment-content">{comment.content}</p>
        <div className="comment-actions">
          <button
            className={`comment-like-btn ${hasLiked ? 'liked' : ''}`}
            onClick={() => onLike(comment.id)}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill={hasLiked ? 'currentColor' : 'none'}
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
            </svg>
            <span>{likeCount}</span>
          </button>
          {canDelete && (
            <button
              className="comment-delete-btn"
              onClick={() => onDelete(comment.id)}
              title="Delete comment"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M3 6h18" />
                <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </motion.div>
  )
}

function SignInForm({ onSuccess, draftContent, setDraftContent }) {
  const { login, loading: authLoading, error: authError, clearError } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!email.trim() || !password.trim()) return

    setIsSubmitting(true)
    setError('')
    if (authError) clearError()

    const result = await login(email, password)

    if (result.success) {
      onSuccess()
    } else {
      setError(result.error || 'Invalid email or password')
    }
    setIsSubmitting(false)
  }

  return (
    <div className="comment-signin-section">
      <div className="signin-prompt">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
        <h4>Sign in to comment</h4>
        <p>Join the conversation by signing in to your AI4Solar account.</p>
      </div>

      <form className="comment-signin-form" onSubmit={handleSubmit}>
        {(error || authError) && (
          <div className="signin-error">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span>{error || authError}</span>
          </div>
        )}

        <div className="signin-form-row">
          <input
            type="email"
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="comment-input"
            required
            autoComplete="email"
          />
        </div>
        <div className="signin-form-row">
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="comment-input"
            required
            autoComplete="current-password"
          />
        </div>

        {/* Draft comment preview */}
        {draftContent && (
          <div className="draft-preview">
            <label>Your draft comment:</label>
            <textarea
              value={draftContent}
              onChange={(e) => setDraftContent(e.target.value)}
              className="comment-textarea"
              rows={3}
            />
          </div>
        )}

        <button
          type="submit"
          className="comment-submit-btn signin-submit-btn"
          disabled={isSubmitting || authLoading || !email.trim() || !password.trim()}
        >
          {isSubmitting || authLoading ? 'Signing in...' : 'Sign In & Comment'}
        </button>

        <p className="signin-footer">
          Don't have an account? <a href="/register">Create one</a>
        </p>
      </form>
    </div>
  )
}

function CommentForm({ onSubmit, userName, initialContent = '' }) {
  const [content, setContent] = useState(initialContent)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Update content when initialContent changes (after sign-in)
  useEffect(() => {
    if (initialContent) {
      setContent(initialContent)
    }
  }, [initialContent])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!content.trim()) return

    setIsSubmitting(true)

    // Simulate a slight delay for UX
    await new Promise(resolve => setTimeout(resolve, 300))

    onSubmit(userName, content.trim())
    setContent('')
    // Clear draft from localStorage
    try {
      localStorage.removeItem(DRAFT_COMMENT_KEY)
    } catch (e) {
      // Ignore
    }
    setIsSubmitting(false)
  }

  // Save draft as user types
  const handleContentChange = (e) => {
    const newContent = e.target.value
    setContent(newContent)
    try {
      if (newContent.trim()) {
        localStorage.setItem(DRAFT_COMMENT_KEY, newContent)
      } else {
        localStorage.removeItem(DRAFT_COMMENT_KEY)
      }
    } catch (err) {
      // Ignore localStorage errors
    }
  }

  return (
    <form className="comment-form" onSubmit={handleSubmit}>
      <h4>Leave a Comment</h4>
      <div className="comment-form-row commenting-as">
        <span>Commenting as <strong>{userName}</strong></span>
      </div>
      <div className="comment-form-row">
        <textarea
          placeholder="Share your thoughts..."
          value={content}
          onChange={handleContentChange}
          className="comment-textarea"
          rows={4}
          required
        />
      </div>
      <button
        type="submit"
        className="comment-submit-btn"
        disabled={isSubmitting || !content.trim()}
      >
        {isSubmitting ? 'Posting...' : 'Post Comment'}
      </button>
    </form>
  )
}

export default function BlogComments({ postId }) {
  const { isAuthenticated, user, loading: authLoading } = useAuth()
  const {
    comments,
    addComment,
    deleteComment,
    toggleLike,
    hasLiked,
    getLikeCount,
    commentCount
  } = useBlogComments(postId)

  // Draft comment state (for preserving text during sign-in)
  const [draftContent, setDraftContent] = useState(() => {
    try {
      return localStorage.getItem(DRAFT_COMMENT_KEY) || ''
    } catch {
      return ''
    }
  })

  // Get user display name
  const getUserName = () => {
    if (!user) return ''
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`
    }
    if (user.first_name) return user.first_name
    if (user.username) return user.username
    return user.email?.split('@')[0] || 'User'
  }

  // Handle successful sign-in
  const handleSignInSuccess = () => {
    // Draft is already preserved in state, form will use it
  }

  // Handle comment submission
  const handleAddComment = (author, content) => {
    addComment(author, content)
    setDraftContent('')
  }

  if (authLoading) {
    return (
      <section className="blog-comments-section">
        <div className="comments-header">
          <h3>Comments</h3>
        </div>
        <div className="comments-loading">Loading...</div>
      </section>
    )
  }

  return (
    <section className="blog-comments-section" id="comments">
      <div className="comments-header">
        <h3>Comments ({commentCount})</h3>
      </div>

      <div className="comments-list">
        {comments.length > 0 ? (
          comments.map(comment => (
            <Comment
              key={comment.id}
              comment={comment}
              onLike={toggleLike}
              hasLiked={hasLiked(comment.id)}
              likeCount={getLikeCount(comment.id, comment.likes)}
              onDelete={deleteComment}
              canDelete={comment.isUserComment}
            />
          ))
        ) : (
          <p className="no-comments">Be the first to comment on this article.</p>
        )}
      </div>

      {isAuthenticated ? (
        <CommentForm
          onSubmit={handleAddComment}
          userName={getUserName()}
          initialContent={draftContent}
        />
      ) : (
        <SignInForm
          onSuccess={handleSignInSuccess}
          draftContent={draftContent}
          setDraftContent={setDraftContent}
        />
      )}
    </section>
  )
}
